import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { siteInfo } from "@/mocks/site";

type FormStatus = "idle" | "submitting" | "success" | "error";

interface FormResponseBody {
  code?: string;
  meta?: { message?: string; detail?: string };
  message?: string;
}

const FORM_ENDPOINT = "https://readdy.ai/api/form/dahga83oh653ivfvnu80";
const MAX_MESSAGE = 500;

const contactWays = [
  {
    icon: "ri-edit-box-line",
    title: "在线投稿 / 反馈表单",
    desc: "最推荐的方式。填写后立即进入编辑队列，无需注册。",
    value: "本页下方表单",
  },
  {
    icon: "ri-mail-send-line",
    title: "编辑邮箱",
    desc: "材料较多、需要附压缩包时，直接发邮件最方便。",
    value: siteInfo.email,
  },
  {
    icon: "ri-time-line",
    title: "值班时间",
    desc: "线索接收全年无休；编辑人工回复集中在工作日。",
    value: siteInfo.workTime,
  },
];

export default function ContactPage() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  useSeo({
    title: "联系我们 | 合作·纠错·删稿申请 - 直曝",
    description:
      "联系直曝编辑部：合作洽谈、内容纠错、删稿申请与使用反馈，都可以通过联系表单或编辑邮箱提交。工作日 10:00 - 19:00 人工回复，投稿类线索全年无休接收。",
    keywords: "网络爆料邮箱,网络爆料后台,联系我们,内容纠错,删稿申请",
    path: "/contact",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系直曝编辑部",
        url: "https://ygtabln.cn/contact",
        inLanguage: "zh-CN",
        description: "合作洽谈、内容纠错、删稿申请与使用反馈，均可通过联系表单或编辑邮箱提交。",
        mainEntity: {
          "@type": "Organization",
          name: siteInfo.fullName,
          email: siteInfo.email,
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "编辑与线索受理",
            email: siteInfo.email,
            availableLanguage: ["zh-CN"],
          },
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://ygtabln.cn/" },
          { "@type": "ListItem", position: 2, name: "联系我们", item: "https://ygtabln.cn/contact" },
        ],
      },
    ],
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guardValue = String(formData.get("contact_alt") ?? "").trim();
    if (guardValue) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponseBody | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponseBody;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMessage === "string" &&
        (serverMessage.includes("spam") || serverMessage.includes("form data is spam"));

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setMessageLength(0);
      } else {
        setStatus("error");
        setFormError(serverMessage || "提交未能成功，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络连接异常，消息尚未送出，请稍后重试。");
    }
  };

  return (
    <>
      <div className="bg-background-100 border-b border-background-200">
        <div className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-6xl mx-auto">
            <Breadcrumbs items={[{ label: "联系我们" }]} />
            <h1 className="mt-5 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
              联系我们
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
              合作、纠错、删稿申请或单纯想提个建议，都可以从这里开始。投递线索请优先使用{" "}
              <Link to="/submit" className="text-primary-600 hover:underline cursor-pointer">
                投稿通道
              </Link>
              。
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-7">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">给编辑部留言</h2>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                请说明你的来意与涉及的文章（如有），便于编辑快速定位。
              </p>

              {status === "success" ? (
                <div className="mt-6 rounded-lg border border-secondary-300 bg-secondary-50 p-6 text-center">
                  <span className="mx-auto w-12 h-12 rounded-full bg-secondary-500 text-background-50 flex items-center justify-center">
                    <i className="ri-check-line text-xl" aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">消息已送达</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    编辑部会按提交顺序处理，工作日一般在三个工作日内回复。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-foreground-950 text-background-50 text-sm font-label font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    再留一条留言
                  </button>
                </div>
              ) : (
                <form
                  id="contact-editor-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5"
                >
                  <div>
                    <label htmlFor="contact_name" className="block text-sm font-label font-medium text-foreground-900 mb-2">
                      怎么称呼你 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="contact_name"
                      name="name"
                      type="text"
                      required
                      maxLength={40}
                      placeholder="可以填化名"
                      className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact_email" className="block text-sm font-label font-medium text-foreground-900 mb-2">
                      邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="contact_email"
                      name="email"
                      type="email"
                      required
                      maxLength={80}
                      placeholder="便于我们回复你"
                      className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="contact_topic" className="block text-sm font-label font-medium text-foreground-900 mb-2">
                      主题 <span className="text-primary-600">*</span>
                    </label>
                    <select
                      id="contact_topic"
                      name="topic"
                      required
                      defaultValue=""
                      className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 cursor-pointer"
                    >
                      <option value="" disabled>
                        请选择一个主题
                      </option>
                      <option value="内容纠错">内容纠错</option>
                      <option value="删稿或下架申请">删稿或下架申请</option>
                      <option value="内容合作">内容合作</option>
                      <option value="使用反馈">使用反馈</option>
                      <option value="其他来意">其他来意</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="contact_message" className="block text-sm font-label font-medium text-foreground-900 mb-2">
                      具体说明 <span className="text-primary-600">*</span>
                    </label>
                    <textarea
                      id="contact_message"
                      name="message"
                      required
                      rows={5}
                      maxLength={MAX_MESSAGE}
                      onChange={(e) => setMessageLength(e.target.value.length)}
                      placeholder="如需纠错，请注明文章标题与具体段落；如需删稿，请说明涉及内容与理由。"
                      className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 resize-none"
                    ></textarea>
                    <div className="mt-1.5 flex items-center justify-between text-xs text-foreground-500 font-label">
                      <span>最多 {MAX_MESSAGE} 字</span>
                      <span className={messageLength > MAX_MESSAGE - 50 ? "text-primary-600" : ""}>
                        {messageLength} / {MAX_MESSAGE}
                      </span>
                    </div>
                  </div>

                  <div className="addr-assist-note" aria-hidden="true">
                    <label htmlFor="contact_alt">备用联系电话</label>
                    <input
                      id="contact_alt"
                      name="contact_alt"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                      aria-hidden="true"
                    />
                  </div>

                  {status === "error" && (
                    <p className="md:col-span-2 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                      <i className="ri-error-warning-line w-5 h-5 flex items-center justify-center shrink-0" aria-hidden="true"></i>
                      <span>{formError || "提交未能成功，请稍后重试。"}</span>
                    </p>
                  )}

                  <div className="md:col-span-2">
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin" aria-hidden="true"></i>
                          正在发送…
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-fill w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
                          发送留言
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </section>
          </div>

          <aside className="lg:col-span-5 flex flex-col gap-6">
            {contactWays.map((way) => (
              <section key={way.title} className="rounded-lg border border-background-200 bg-background-100 p-5">
                <div className="flex items-start gap-4">
                  <span className="w-11 h-11 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center shrink-0">
                    <i className={`${way.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <div className="flex-1">
                    <h2 className="font-heading text-base font-bold text-foreground-950">{way.title}</h2>
                    <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{way.desc}</p>
                    <p className="mt-2.5 text-sm font-label text-primary-700 break-all">{way.value}</p>
                  </div>
                </div>
              </section>
            ))}

            <section className="rounded-lg bg-foreground-950 p-5 text-background-50">
              <h2 className="font-heading text-base font-bold flex items-center gap-2">
                <i className="ri-shield-keyhole-line w-5 h-5 flex items-center justify-center text-accent-400" aria-hidden="true"></i>
                关于投稿类来信
              </h2>
              <p className="mt-3 text-sm text-background-300 leading-relaxed">
                如果你是要提供线索，请使用投稿通道。本页表单面向合作、纠错与反馈，投稿内容可能会因此延迟处理。
              </p>
              <Link
                to="/submit"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-send-plane-fill w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
                前往投稿通道
              </Link>
            </section>
          </aside>
        </div>
      </div>
    </>
  );
}